/**
 * Meta Pixel utility functions for tracking events
 * Requires Meta Pixel base script to be loaded in index.html
 * Note: Meta Pixel is initialized in index.html with hardcoded Pixel ID
 */

// Check if fbq is available
const isPixelAvailable = () => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

/**
 * Initialize Meta Pixel
 * Note: This is a no-op as Meta Pixel is initialized in index.html
 * Kept for backwards compatibility but does nothing
 */
export const initMetaPixel = () => {
  // Meta Pixel is initialized in index.html, no action needed
};

/**
 * Track PageView event
 * @param {string} url - Optional URL to track
 */
export const trackPageView = (url) => {
  if (!isPixelAvailable()) {
    return;
  }

  try {
    if (url) {
      window.fbq('track', 'PageView', {}, { eventID: url });
    } else {
      window.fbq('track', 'PageView');
    }
  } catch (error) {
    console.error('Error tracking PageView:', error);
  }
};

/**
 * Track ViewContent event (when viewing a product)
 * @param {Object} product - Product object with id, name, price, etc.
 */
export const trackViewContent = (product) => {
  if (!isPixelAvailable()) {
    return;
  }

  if (!product) {
    console.warn('Product data is required for ViewContent event');
    return;
  }

  try {
    const contentData = {
      content_name: product.name,
      content_ids: [product._id || product.id],
      content_type: 'product',
      value: product.price,
      currency: 'TRY',
    };

    window.fbq('track', 'ViewContent', contentData);
  } catch (error) {
    console.error('Error tracking ViewContent:', error);
  }
};

/**
 * Track AddToCart event
 * @param {Object} product - Product object
 * @param {number} quantity - Quantity added to cart
 */
export const trackAddToCart = (product, quantity = 1) => {
  if (!isPixelAvailable()) {
    return;
  }

  if (!product) {
    console.warn('Product data is required for AddToCart event');
    return;
  }

  try {
    const cartData = {
      content_name: product.name,
      content_ids: [product._id || product.id],
      content_type: 'product',
      value: product.price * quantity,
      currency: 'TRY',
      quantity: quantity,
    };

    window.fbq('track', 'AddToCart', cartData);
  } catch (error) {
    console.error('Error tracking AddToCart:', error);
  }
};

/**
 * Track InitiateCheckout event
 * @param {number} value - Total cart value
 * @param {number} numItems - Number of items in cart
 * @param {Array} contents - Array of cart items
 */
export const trackInitiateCheckout = (value, numItems, contents = []) => {
  if (!isPixelAvailable()) {
    return;
  }

  try {
    const checkoutData = {
      value: value,
      currency: 'TRY',
      num_items: numItems,
      contents: contents,
    };

    window.fbq('track', 'InitiateCheckout', checkoutData);
  } catch (error) {
    console.error('Error tracking InitiateCheckout:', error);
  }
};

/**
 * Track Purchase event
 * @param {Object} orderData - Order data with id, value, currency, items
 */
export const trackPurchase = (orderData) => {
  if (!isPixelAvailable()) {
    return;
  }

  if (!orderData) {
    console.warn('Order data is required for Purchase event');
    return;
  }

  try {
    const items = orderData.items || orderData.orderItems || [];
    
    // Format contents array for Meta Pixel
    const contents = items.map(item => {
      const productId = item.product?._id || item.product?.id || item.productId?._id || item.productId?.id;
      const price = item.price || item.product?.price || item.productId?.price || 0;
      const quantity = item.quantity || 0;
      
      return {
        id: productId,
        quantity: quantity,
        item_price: price,
      };
    }).filter(item => item.id); // Filter out items without IDs

    const purchaseData = {
      value: orderData.total || orderData.value,
      currency: orderData.currency || 'TRY',
      contents: contents,
      content_ids: contents.map(item => item.id),
      num_items: contents.reduce((sum, item) => sum + item.quantity, 0),
    };

    // Add order ID if available
    if (orderData.orderId || orderData._id || orderData.orderNumber) {
      purchaseData.order_id = orderData.orderId || orderData._id || orderData.orderNumber;
    }

    // Deduplication with the server-side Conversions API Purchase event:
    // both must use the order's _id as the event id (browser eventID === server
    // event_id), so Meta counts a single Purchase when both fire.
    const eventId = orderData._id || orderData.orderId || orderData.orderNumber;

    if (eventId) {
      window.fbq('track', 'Purchase', purchaseData, { eventID: String(eventId) });
    } else {
      window.fbq('track', 'Purchase', purchaseData);
    }
  } catch (error) {
    console.error('Error tracking Purchase:', error);
  }
};

